import { useState } from 'react';

import { useAuth } from '../hooks/use-auth';
import { useHandoffs } from '../hooks/use-handoffs';

import { CapabilityGate } from './CapabilityGate';
import { HandoffAcceptRejectModal } from './HandoffAcceptRejectModal';

import type { HandoffWireItem } from '../lib/api-client';

/**
 * NotificationsTray — Fatia G.
 *
 * Sino do header — mostra o contador de handoffs `pending` dirigidos ao user e,
 * ao clicar, abre o modal Aceitar/Recusar do mais antigo.
 *
 * Noturna 04/Set/2026 (A-35): este componente existia desde a Fatia G e NÃO
 * estava montado em lugar nenhum — a Topbar tinha um sino «(placeholder)».
 * Medido: `POST /v1/handoffs` criava a transferência push e o destinatário não
 * via nada; a transferência morria pendente. Agora ele vive na Topbar com as
 * classes do DS (`topbar__icon-btn` + `topbar__badge`), sem estilo inline.
 *
 * v1: foca em handoffs push. Pull queue + outros tipos de notificação entram em
 * refinamento (Item Revisitação: bandeja consolidada).
 */
export function NotificationsTray(): JSX.Element {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const { data } = useHandoffs({ status: 'pending', forUser: userId });
  const [activeHandoff, setActiveHandoff] = useState<HandoffWireItem | null>(null);
  const count = data.length;
  const label = count > 0 ? `Notificações (${count})` : 'Notificações';
  const title =
    count === 0
      ? 'Sem transferências aguardando você'
      : count === 1
        ? '1 transferência aguardando sua resposta'
        : `${count} transferências aguardando sua resposta`;

  return (
    <CapabilityGate capability="peer-coordination-handoff">
      <button
        type="button"
        className="topbar__icon-btn topbar__icon-btn--badged"
        aria-label={label}
        title={title}
        onClick={() => {
          if (data.length > 0) setActiveHandoff(data[0] ?? null);
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {count > 0 && (
          <span className="topbar__badge" aria-hidden>
            {count}
          </span>
        )}
      </button>
      {activeHandoff !== null && (
        <HandoffAcceptRejectModal
          handoff={activeHandoff}
          open={true}
          onClose={() => setActiveHandoff(null)}
        />
      )}
    </CapabilityGate>
  );
}
